import { LEGACY_START_DATE_QUESTION_ID } from 'jobs/creation/PrefilledDraftInput'
import { PublishedLegend } from 'legends/PublishedLegend'
import { action, computed, makeObservable } from 'mobx'
import { Observable, Subject } from 'rxjs'
import { map } from 'rxjs/operators'
import { maybeParseDate } from 'utils/date-util'
import { fromStream, toStream } from 'utils/mobx-util'
import { filterNonNil, shareLatest, some } from 'utils/rx-util'
import DateTimeQuestionViewModel from '../questions/DateTime/DateTimeQuestionViewModel'
import { QuestionViewModelAdapter } from '../questions/QuestionViewModelBase'
import { StartDateChanged } from './DraftChange'
import { DraftStateSlice, PrepareContext } from './DraftStateSlice'
import { StepContainer, StepFactory, StepViewModel } from './OutlineViewModel'

const TITLE = 'When should this be worked on?'

/**
 * Start Date view model.
 */
export default class StartDateViewModel extends DraftStateSlice {
  /**
   * The step.
   */
  readonly step: StepViewModel

  /**
   * Changes subject.
   * @private
   */
  private readonly _changes = new Subject<Date | null>()

  /**
   * Using a Date VM here.
   */
  readonly dateVM: DateTimeQuestionViewModel = new DateTimeQuestionViewModel(
    QuestionViewModelAdapter(
      () => this._changes.next(this.dateVM.answer ?? null),
      this._asOfDate
    )
  )

  /**
   * Whether scheduling is allowed.
   */
  readonly isSchedulingAllowed$ = this.legend$.pipe(
    filterNonNil(),
    map((legend) => legend.allowScheduling),
    shareLatest(),
    this.whileReady()
  )

  /**
   * Changes observable.
   */
  readonly changes$: Observable<StartDateChanged> = this._changes.pipe(
    map((date) => ({
      type: 'StartDateChanged',
      newStartDate: date,
    }))
  )

  /**
   * Start date as a stream.
   */
  readonly startDate$ = toStream(() => this.dateVM.answer ?? null, true).pipe(
    this.whileReady()
  )

  /**
   * This step is valid when we either have a start date or scheduling is disabled.
   */
  readonly isValid$ = some([
    this.startDate$,
    this.isSchedulingAllowed$.pipe(map((x) => !x)),
  ])

  /**
   * Streams to Observables.
   * @private
   */
  private readonly $ = {
    allQuestionsCompleted: fromStream(false, this.allQuestionsCompleted$),
    legend: fromStream(null, this.legend$),
    isSchedulingAllowed: fromStream(false, this.isSchedulingAllowed$),
  }

  /**
   * Constructor.
   */
  constructor(
    isActive$: Observable<boolean>,
    createStep: StepFactory,
    private allQuestionsCompleted$: Observable<boolean>,
    private legend$: Observable<PublishedLegend | null>,
    private _asOfDate: () => Date,
    private _isDisabled: () => boolean
  ) {
    super(isActive$)
    makeObservable(this)
    this.hydrateQuestionVM()

    // TODO(jeffijoe): This is not optimal since if we switch between legends
    //  then we forget the state. This will go away once we move start date
    //  to a computed field.
    //  See https://taxfyle.atlassian.net/browse/DEV-3806
    this.isSchedulingAllowed$.subscribe(
      (allowed) => !allowed && this.dateVM.change(undefined)
    )

    this.step = createStep({
      stepId: 'scheduling',
      isValid: () =>
        this.isSchedulingAllowed &&
        this.dateVM.isAnswered &&
        this.dateVM.validateResponse().type === 'Success',
      outlineItemDisplay: () => {
        return {
          title: TITLE,
          value: this.dateVM.answer ? this.dateVM.toTextAnswer() : null,
          shortValue: this.dateVM.toTextAnswer(25),
          description:
            'The request will be made available for pickup on this date.',
          visual: this.dateVM.answer
            ? {
                type: 'QuestionIcon',
                questionType: 'DATE_TIME',
              }
            : undefined,
        }
      },
    })
  }

  /**
   * Whether the editor is disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /**
   * Whether scheduling is allowed.
   *
   * @private
   */
  @computed
  get isSchedulingAllowed() {
    return this.$.isSchedulingAllowed()
  }

  /**
   * The step containers.
   */
  @computed
  get stepContainers(): StepContainer[] {
    if (!this.isSchedulingAllowed || !this.$.allQuestionsCompleted()) {
      return []
    }

    return [this]
  }

  /**
   * Prepares the VM.
   *
   * @param context
   */
  async prepare(context: PrepareContext): Promise<void> {
    if (context.type === 'NewDraft') {
      const startDateAnswer = context.prefilledDraft.answers.find(
        (q) => q.id === LEGACY_START_DATE_QUESTION_ID
      )
      this.dateVM.change(maybeParseDate(startDateAnswer?.answer as string))
      return
    }
    this.hydrateQuestionVM(context.job.dateStart ?? undefined)
  }

  /**
   * Hydrates the question VM.
   *
   * @param answer
   */
  @action.bound
  hydrateQuestionVM(answer?: Date) {
    this.dateVM.hydrateDTO({
      answer,
      id: 'LEGACY_START_DATE',
      collect_time: false,
      type: 'DATE_TIME',
      disallow_future: false,
      disallow_past: true,
    })
  }
}
